import type { ConfirmPayload, PromptPayload, AlertPayload, DialogConfig } from './types';

/**
 * Dialog Manager for Account Switcher
 *
 * Provides custom styled dialogs for confirm, prompt, and alert interactions.
 * Ported from the Laravel account-switcher-client.js implementation.
 */
export class DialogManager {
  private activeDialog: HTMLElement | null = null;
  private previousFocus: HTMLElement | null = null;
  private dialogStylesInjected = false;
  private config: DialogConfig;

  constructor(config: DialogConfig = {}) {
    this.config = {
      enabled: config.enabled !== false,
      closeOnOverlayClick: config.closeOnOverlayClick !== false,
      closeOnEscape: config.closeOnEscape !== false,
    };
  }

  /**
   * Show a confirm dialog
   */
  async showConfirm(payload: ConfirmPayload): Promise<boolean> {
    if (this.activeDialog) {
      throw new Error('Another dialog is already open');
    }

    return new Promise((resolve) => {
      const overlay = this.createDialogOverlay();
      const dialog = this.createConfirmDialog(payload);

      const handleConfirm = () => {
        this.destroyDialog(overlay);
        resolve(true);
      };

      const handleCancel = () => {
        this.destroyDialog(overlay);
        resolve(false);
      };

      // Attach button listeners
      dialog.querySelector('.asc-dialog-btn-confirm')?.addEventListener('click', handleConfirm);
      dialog.querySelector('.asc-dialog-btn-cancel')?.addEventListener('click', handleCancel);

      // ESC key handler
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.config.closeOnEscape) {
          document.removeEventListener('keydown', handleKeyDown);
          handleCancel();
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          document.removeEventListener('keydown', handleKeyDown);
          handleConfirm();
        }
      };
      document.addEventListener('keydown', handleKeyDown);

      // Overlay click handler
      if (this.config.closeOnOverlayClick) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            document.removeEventListener('keydown', handleKeyDown);
            handleCancel();
          }
        });
      }

      // Show dialog
      this.previousFocus = document.activeElement as HTMLElement;
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      this.activeDialog = overlay;

      // Set up focus trap and focus first button
      this.trapFocus(dialog);
      (dialog.querySelector('.asc-dialog-btn-confirm') as HTMLElement)?.focus();
    });
  }

  /**
   * Show a prompt dialog
   */
  async showPrompt(payload: PromptPayload): Promise<{ value: string | null; cancelled: boolean }> {
    if (this.activeDialog) {
      throw new Error('Another dialog is already open');
    }

    return new Promise((resolve) => {
      const overlay = this.createDialogOverlay();
      const dialog = this.createPromptDialog(payload);
      const input = dialog.querySelector('.asc-dialog-input') as HTMLInputElement;

      const handleSubmit = () => {
        const value = input.value;
        this.destroyDialog(overlay);
        resolve({ value, cancelled: false });
      };

      const handleCancel = () => {
        this.destroyDialog(overlay);
        resolve({ value: null, cancelled: true });
      };

      // Attach button listeners
      dialog.querySelector('.asc-dialog-btn-confirm')?.addEventListener('click', handleSubmit);
      dialog.querySelector('.asc-dialog-btn-cancel')?.addEventListener('click', handleCancel);

      // Input Enter key handler
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit();
        }
      });

      // ESC key handler
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.config.closeOnEscape) {
          document.removeEventListener('keydown', handleKeyDown);
          handleCancel();
        }
      };
      document.addEventListener('keydown', handleKeyDown);

      // Overlay click handler
      if (this.config.closeOnOverlayClick) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            document.removeEventListener('keydown', handleKeyDown);
            handleCancel();
          }
        });
      }

      // Show dialog
      this.previousFocus = document.activeElement as HTMLElement;
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      this.activeDialog = overlay;

      // Set up focus trap and focus input
      this.trapFocus(dialog);
      input.focus();
      input.select();
    });
  }

  /**
   * Show an alert dialog
   */
  async showAlert(payload: AlertPayload): Promise<void> {
    if (this.activeDialog) {
      throw new Error('Another dialog is already open');
    }

    return new Promise((resolve) => {
      const overlay = this.createDialogOverlay();
      const dialog = this.createAlertDialog(payload);

      const handleOk = () => {
        this.destroyDialog(overlay);
        resolve();
      };

      // Attach button listener
      dialog.querySelector('.asc-dialog-btn-confirm')?.addEventListener('click', handleOk);

      // ESC and Enter key handlers
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.key === 'Escape' && this.config.closeOnEscape) || e.key === 'Enter') {
          e.preventDefault();
          document.removeEventListener('keydown', handleKeyDown);
          handleOk();
        }
      };
      document.addEventListener('keydown', handleKeyDown);

      // Overlay click handler
      if (this.config.closeOnOverlayClick) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            document.removeEventListener('keydown', handleKeyDown);
            handleOk();
          }
        });
      }

      // Show dialog
      this.previousFocus = document.activeElement as HTMLElement;
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      this.activeDialog = overlay;

      // Set up focus trap and focus button
      this.trapFocus(dialog);
      (dialog.querySelector('.asc-dialog-btn-confirm') as HTMLElement)?.focus();
    });
  }

  /**
   * Create dialog overlay backdrop
   */
  private createDialogOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'asc-dialog-overlay';
    overlay.setAttribute('role', 'presentation');
    return overlay;
  }

  /**
   * Create confirm dialog element
   */
  private createConfirmDialog(payload: ConfirmPayload): HTMLElement {
    const dialog = document.createElement('div');
    dialog.className = 'asc-dialog asc-dialog-confirm';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-labelledby', 'asc-dialog-title');
    dialog.setAttribute('aria-modal', 'true');

    const variant = payload.variant || 'info';
    const title = payload.title || 'Confirm';
    const message = payload.message || '';
    const confirmText = payload.confirmText || 'OK';
    const cancelText = payload.cancelText || 'Cancel';

    dialog.innerHTML = `
      <div class="asc-dialog-header">
        <h3 id="asc-dialog-title" class="asc-dialog-title"></h3>
      </div>
      <div class="asc-dialog-body">
        <p class="asc-dialog-message"></p>
      </div>
      <div class="asc-dialog-footer">
        <button class="asc-dialog-btn asc-dialog-btn-cancel" type="button"></button>
        <button class="asc-dialog-btn asc-dialog-btn-confirm asc-dialog-btn-${variant}" type="button"></button>
      </div>
    `;

    // Set text content safely (XSS protection)
    dialog.querySelector('#asc-dialog-title')!.textContent = title;
    dialog.querySelector('.asc-dialog-message')!.textContent = message;
    dialog.querySelector('.asc-dialog-btn-cancel')!.textContent = cancelText;
    dialog.querySelector('.asc-dialog-btn-confirm')!.textContent = confirmText;

    return dialog;
  }

  /**
   * Create prompt dialog element
   */
  private createPromptDialog(payload: PromptPayload): HTMLElement {
    const dialog = document.createElement('div');
    dialog.className = 'asc-dialog asc-dialog-prompt';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-labelledby', 'asc-dialog-title');
    dialog.setAttribute('aria-modal', 'true');

    const title = payload.title || 'Input Required';
    const message = payload.message || '';
    const defaultValue = payload.defaultValue || '';
    const placeholder = payload.placeholder || '';

    dialog.innerHTML = `
      <div class="asc-dialog-header">
        <h3 id="asc-dialog-title" class="asc-dialog-title"></h3>
      </div>
      <div class="asc-dialog-body">
        <p class="asc-dialog-message"></p>
        <input type="text" class="asc-dialog-input" value="" placeholder="">
      </div>
      <div class="asc-dialog-footer">
        <button class="asc-dialog-btn asc-dialog-btn-cancel" type="button">Cancel</button>
        <button class="asc-dialog-btn asc-dialog-btn-confirm" type="button">Submit</button>
      </div>
    `;

    // Set text content safely (XSS protection)
    dialog.querySelector('#asc-dialog-title')!.textContent = title;
    dialog.querySelector('.asc-dialog-message')!.textContent = message;
    (dialog.querySelector('.asc-dialog-input') as HTMLInputElement).value = defaultValue;
    (dialog.querySelector('.asc-dialog-input') as HTMLInputElement).placeholder = placeholder;

    return dialog;
  }

  /**
   * Create alert dialog element
   */
  private createAlertDialog(payload: AlertPayload): HTMLElement {
    const dialog = document.createElement('div');
    dialog.className = 'asc-dialog asc-dialog-alert';
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-labelledby', 'asc-dialog-title');
    dialog.setAttribute('aria-modal', 'true');

    const variant = payload.variant || 'info';
    const title = payload.title || 'Alert';
    const message = payload.message || '';

    dialog.innerHTML = `
      <div class="asc-dialog-header">
        <h3 id="asc-dialog-title" class="asc-dialog-title"></h3>
      </div>
      <div class="asc-dialog-body">
        <p class="asc-dialog-message"></p>
      </div>
      <div class="asc-dialog-footer">
        <button class="asc-dialog-btn asc-dialog-btn-confirm asc-dialog-btn-${variant}" type="button">OK</button>
      </div>
    `;

    // Set text content safely (XSS protection)
    dialog.querySelector('#asc-dialog-title')!.textContent = title;
    dialog.querySelector('.asc-dialog-message')!.textContent = message;

    return dialog;
  }

  /**
   * Destroy and remove a dialog
   */
  private destroyDialog(overlay: HTMLElement): void {
    if (!overlay || !overlay.parentNode) return;

    // Restore focus
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      try {
        this.previousFocus.focus();
      } catch (e) {
        // Element may no longer be focusable
      }
    }

    // Remove from DOM
    overlay.parentNode.removeChild(overlay);
    this.activeDialog = null;
    this.previousFocus = null;
  }

  /**
   * Trap focus within dialog for accessibility
   */
  private trapFocus(dialog: HTMLElement): void {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusableElements = dialog.querySelectorAll(focusableSelectors);
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    dialog.addEventListener('keydown', handleTabKey);
  }

  /**
   * Inject dialog CSS styles into the document
   */
  injectDialogStyles(): void {
    if (this.dialogStylesInjected) return;

    const style = document.createElement('style');
    style.id = 'asc-dialog-styles';
    style.textContent = `
/* Account Switcher Client Dialog Styles */
.asc-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: asc-fade-in 200ms ease-out;
  padding: 16px;
}

.asc-dialog {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 400px;
  width: 100%;
  max-height: calc(100vh - 32px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: asc-scale-in 250ms ease-out;
}

.asc-dialog-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.asc-dialog-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.asc-dialog-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.asc-dialog-message {
  margin: 0 0 16px 0;
  color: #4b5563;
  line-height: 1.6;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.asc-dialog-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  transition: border-color 150ms, box-shadow 150ms;
  box-sizing: border-box;
}

.asc-dialog-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.asc-dialog-footer {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.asc-dialog-btn {
  padding: 0 24px;
  height: 40px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms;
  white-space: nowrap;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.asc-dialog-btn:focus {
  outline: 2px solid;
  outline-offset: 2px;
}

.asc-dialog-btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.asc-dialog-btn-cancel:hover {
  background: #e5e7eb;
}

.asc-dialog-btn-cancel:focus {
  outline-color: #9ca3af;
}

.asc-dialog-btn-confirm {
  background: #3b82f6;
  color: white;
}

.asc-dialog-btn-confirm:hover {
  background: #2563eb;
}

.asc-dialog-btn-confirm:focus {
  outline-color: #3b82f6;
}

.asc-dialog-btn-info { background: #3b82f6; }
.asc-dialog-btn-info:hover { background: #2563eb; }
.asc-dialog-btn-info:focus { outline-color: #3b82f6; }

.asc-dialog-btn-warning { background: #f59e0b; }
.asc-dialog-btn-warning:hover { background: #d97706; }
.asc-dialog-btn-warning:focus { outline-color: #f59e0b; }

.asc-dialog-btn-danger { background: #ef4444; }
.asc-dialog-btn-danger:hover { background: #dc2626; }
.asc-dialog-btn-danger:focus { outline-color: #ef4444; }

.asc-dialog-btn-error { background: #ef4444; }
.asc-dialog-btn-error:hover { background: #dc2626; }
.asc-dialog-btn-error:focus { outline-color: #ef4444; }

.asc-dialog-btn-success { background: #10b981; }
.asc-dialog-btn-success:hover { background: #059669; }
.asc-dialog-btn-success:focus { outline-color: #10b981; }

@keyframes asc-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes asc-scale-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 480px) {
  .asc-dialog-footer {
    flex-direction: column-reverse;
  }
  .asc-dialog-btn {
    width: 100%;
  }
}
    `;
    document.head.appendChild(style);
    this.dialogStylesInjected = true;
  }
}
